import * as React from "react";
import {
  CalendarEvent,
  CalendarEventDetails,
  Post,
  CALENDAR_EVENT_SOURCE_METADATA,
  UserGroups,
  SocialNetworkPostStatus,
  SocialNetworkPosted,
} from "@/interfaces";
import "./styles.scss";
import { Button, Header, Icon, Modal } from "semantic-ui-react";
import { useEffect } from "react";
import { CalendarEventServices } from "@/services/CalendarEventServices";
import { AppContext } from "@/providers/AppContextProvider";
import { ReschedulePostsModal } from "./ReschedulePostsModal";
import { DeletePostsModal } from "./DeletePostsModal";
import { PostsTable } from "./PostsTable";
import { PushTable } from "./PushTable";
import { formatDate, getUrlForSource, wrapInLinkIfHrefIsPresent } from "./utils";

type Props = {
  onClose: () => void;
  onUpdate: () => void;
  event: CalendarEvent | null;
  handlePost: (post: Post, socialPost?: SocialNetworkPosted) => void;
};

export const EventModal: React.FC<Props> = ({ onClose, onUpdate, event, handlePost }) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [eventDetails, setEventDetails] = React.useState<CalendarEventDetails>();
  const [showRescheduleModal, setShowRescheduleModal] = React.useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
  const { userContext } = React.useContext(AppContext);
  const [selectedPosts, setSelectedPosts] = React.useState<Post[]>([]);
  const { label, icon } = CALENDAR_EVENT_SOURCE_METADATA[event?.sourceType || "CAMPAIGN"];
  const isAdmin = userContext?.account.groups.some(g => g === UserGroups.ADMIN);

  const loadCalendarEventDetails = () => {
    if (!event) return;

    setIsLoading(true);
    CalendarEventServices.getCalendarEventDetails(event.id)
      .then(response => {
        response.posts.forEach(post => {
          post.socialNetworks.forEach(sn => {
            if (sn.status === SocialNetworkPostStatus.FAILURE_ACKED) sn.status = SocialNetworkPostStatus.FAILED;
          });
        });
        setEventDetails(response);
      })
      .catch(e => console.error(e))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setEventDetails(undefined);
    setShowRescheduleModal(false);
    setShowDeleteModal(false);
    loadCalendarEventDetails();
  }, [event]);

  const clearSelectedPosts = () => {
    const postIds = selectedPosts.map(p => p.postId);
    setEventDetails(currentEventDetails => {
      if (!currentEventDetails) return currentEventDetails;
      const newEventDetails = { ...currentEventDetails };
      newEventDetails.posts = newEventDetails.posts.filter(post => !postIds.includes(post.postId));
      // If there are no longer posts to display, auto close the modal
      if (newEventDetails.posts.length === 0) onClose();
      return newEventDetails;
    });
  };

  const handlePostsUpdated = () => {
    clearSelectedPosts();
    onUpdate();
    setShowRescheduleModal(false);
    setShowDeleteModal(false);
  };

  return (
    <>
      <ReschedulePostsModal
        open={event !== null && showRescheduleModal}
        posts={selectedPosts}
        currentScheduledDateTime={event?.scheduledDateTime ? new Date(event.scheduledDateTime) : new Date()}
        onClose={() => setShowRescheduleModal(false)}
        onPostsRescheduled={handlePostsUpdated}
      />

      <DeletePostsModal
        open={event !== null && showDeleteModal}
        posts={selectedPosts}
        onClose={() => setShowDeleteModal(false)}
        onPostsDeleted={handlePostsUpdated}
      />

      <Modal
        open={event !== null}
        closeIcon
        onClose={onClose}
        size="fullscreen"
        className={`eventModal ${event?.sourceType.toLowerCase() || ""}`}
      >
        <Modal.Header>
          <Icon name={icon} />
          {label}
          <div className="grow" />
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <Header>Details</Header>
            <div className="eventModalDetails">
              <div>
                <span className="detailName">Source:</span>
                <span className="detailValue">
                  {event &&
                    wrapInLinkIfHrefIsPresent(
                      getUrlForSource(userContext, event.sourceType, event.sourceId),
                      <>
                        {event.sourceTitle}
                        {isAdmin && " (ID: " + event.sourceId + ")"}
                      </>
                    )}
                </span>
              </div>
              <div>
                <span className="detailName">Scheduled Date:</span>
                <span className="detailValue">{formatDate(event?.scheduledDateTime)}</span>
              </div>
            </div>

            <PushTable isLoading={isLoading} eventDetails={eventDetails} />

            <Header className="postsHeader">
              Posts
              {selectedPosts.length > 0 && (
                <div className="selectedPostsButtons">
                  <Button icon="pencil" primary onClick={() => setShowRescheduleModal(true)} content="Reschedule" />
                  <Button icon="trash" color="red" onClick={() => setShowDeleteModal(true)} content="Delete" />
                </div>
              )}
            </Header>
            <PostsTable
              isLoading={isLoading}
              eventDetails={eventDetails}
              onSelectedPostsChange={setSelectedPosts}
              handlePost={handlePost}
            />
          </Modal.Description>
        </Modal.Content>
      </Modal>
    </>
  );
};
